import { useSelector } from "react-redux";
import React, { useCallback, useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { selectUserId } from "redux/slices/userSlice";
import { RecruiterActivity } from "redux/models/recruiterActivity";
import { usePageFilter } from "hooks/usePageFilters";
import { ServerSideDataGrid } from "common/components/serverSideDataGrid/serverSideDataGrid";
import { Tooltip } from "common/components/tooltip/tooltip";
import { InfoIcon } from "common/icons/InfoIcon";
import { Section } from "features/dashboard/components/section";
import { SectionError } from "features/dashboard/components/sectionError";
import {
  PipelineCandidatesColumns,
  getPipelineCandidateRowId,
} from "features/dashboard/sections/candidates/table/columns";
import { PipelineCandidateFilters } from "features/dashboard/sections/candidates/filters/filters";
import K from "utilities/constants";
import styles from "features/dashboard/sections/candidates/pipelineCandidates.module.scss";

const FILTER_PREFIX = "pipeline_candidates";

const INFO_TEXT =
  "Days in Status is calculated using the Status Change date for all statuses, except Offer Accepted, which uses the Pending Start date.";

export const PipelineCandidates = ({ clientId, recruiterId }) => {
  const userId = useSelector(selectUserId);
  const history = useHistory();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const gridRef = useRef(null);

  const [statusCounts, setStatusCounts] = useState([]);
  const [search, setSearch] = usePageFilter(`${FILTER_PREFIX}_search`);
  const [candidateStatus, setCandidateStatus] = usePageFilter(
    `${FILTER_PREFIX}_status`,
  );
  const prevRecruiterIdRef = useRef(recruiterId);

  const getData = useCallback(async (params) => {
    setLoading(true);
    setError(null);

    try {
      const { data, total, meta } =
        await RecruiterActivity.getPipelineCandidates(
          params.clientId,
          params.recruiterId,
          params,
          false,
        );

      setStatusCounts(meta?.statusCounts || []);

      return {
        data: data || [],
        total: total || 0,
      };
    } catch (err) {
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const getServerSideData = useCallback(
    (options) => {
      return getData({
        ...options,
        clientId,
        recruiterId,
        ...(search && { search }),
        status: candidateStatus ?? undefined,
      });
    },
    [getData, clientId, recruiterId, search, candidateStatus],
  );

  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    gridRef.current?.api?.refreshServerSide({ purge: true });
  }, []);

  const handleDataFetchFailed = useCallback((err) => {
    setError(err);
  }, []);

  const handleRowClicked = useCallback(
    (event) => {
      const { id, candidate } = event.data;

      if (id && candidate.id) {
        history.push(
          `/candidates/detail/${candidate.id}?tab=jobs&jobEntry=${id}`,
        );
      }
    },
    [history],
  );

  useEffect(() => {
    if (prevRecruiterIdRef.current === recruiterId) return;
    prevRecruiterIdRef.current = recruiterId;

    setStatusCounts([]);
    setCandidateStatus(null);
  }, [recruiterId, setCandidateStatus]);

  if (!clientId || !recruiterId) {
    return (
      <Section title="Pipeline & Candidate Management">
        <div className={styles.pipelineCandidateManagement__emptyState}>
          Please select a client and recruiter to view pipeline candidates.
        </div>
      </Section>
    );
  }

  return (
    <Section
      title="Pipeline & Candidate Management"
      contentMinHeight="458px"
      extra={
        <Tooltip title={INFO_TEXT} placement="left">
          <InfoIcon
            className={`text-primary-accent ${styles.monthlyGoals__infoIcon}`}
          />
        </Tooltip>
      }
    >
      <PipelineCandidateFilters
        search={search}
        onSearchChange={setSearch}
        statusCounts={statusCounts}
        status={candidateStatus}
        onStatusChange={setCandidateStatus}
        disabled={loading}
      />
      {error ? (
        <SectionError
          onRetry={handleRetry}
          message={
            error?.message ||
            "Failed to load pipeline candidates. Please try again."
          }
        />
      ) : (
        <ServerSideDataGrid
          ref={gridRef}
          userId={userId}
          configKey={K.AgGridTable.Keys.PipelineCandidatesVisibleColumn}
          defaultColumnConfig={PipelineCandidatesColumns}
          getData={getServerSideData}
          getRowId={getPipelineCandidateRowId}
          height={410}
          paginationPageSize={7}
          rowClass={styles.pipelineCandidateManagement__row}
          onRowClicked={handleRowClicked}
          onDataFetchFailed={handleDataFetchFailed}
          useGlobalLoading={false}
          showTableLoadingOverlay
          useCustomLoader
        />
      )}
    </Section>
  );
};
